/*
 Router
 Simple router for loading server-generated templates
 and binding views
*/

import { head } from 'assets/components';

import {
  createSafeSlug,
  markActiveLinks,
  poly,
  polyfillObjectAssign, // eslint-disable-line no-unused-vars
} from 'assets/utilities';

// Views
import * as views from 'assets/views';

const state = {
  path: window.location.pathname,
  previousPath: false,
  pageViews: 0,
};

const callbacks = [];

let currentView;
let currentState = window.location.pathname + window.location.search;

const TRANSITION_DURATION = 400;
let transitionTimer = 0;

function onLinkClick(e) {
  const href = this.getAttribute('href');
  if (href === '#' || href.substring(0, 4) === 'http') {
    return;
  }
  e.preventDefault();
  window.history.pushState(null, null, href);
  onState(); // eslint-disable-line no-use-before-define
}

function changeState(path) {
  window.history.pushState(null, null, path);
  onState(); // eslint-disable-line no-use-before-define
}

function bind() {
  if (
    (document.location.href.indexOf('/checkout/') !== -1)
    || (document.location.pathname.indexOf('/tools/') === 0)
  ) {
    return;
  }

  markActiveLinks();
  if (window.settings && window.settings.isHistoryDisabled) {
    return;
  }

  [].forEach.call(document.querySelectorAll('a'), (el) => {
    const href = el.getAttribute('href') || '';

    if (
      el.getAttribute('data-no-history') ||
      el.getAttribute('data-method') === 'delete' ||
      href[0] === '#' ||
      href.substring(0, 4) === 'tel:' ||
      href.substring(0, 4) === 'http' ||
      href.substring(0, 7) === 'mailto:'
    ) {
      return;
    }
    el.removeEventListener('click', onLinkClick);
    el.addEventListener('click', onLinkClick);
  });
}

function getQuery(search = '') {
  if (!search) {
    return '';
  }

  const output = {};
  const pairs = search.split('&');
  pairs.forEach((pair) => {
    const [key, val] = pair.split('=');
    if (key) {
      output[createSafeSlug(key)] = createSafeSlug(val);
    }
  });

  return output;
}

function getProps() {
  state.search = getQuery(window.location.search);

  return {
    bind,
    state,
    changeState,
    onState, // eslint-disable-line no-use-before-define
  };
}

function triggerCallbacks() {
  callbacks.forEach((callback) => {
    callback(getProps());
  });
}

function addCallback(func) {
  callbacks.push(func);
}

function initView() {
  const viewEl = document.querySelector('[data-view]');

  // save the state
  state.pageViews++;
  state.previousPath = state.path;
  state.path = window.location.pathname;

  if (currentView && currentView.destroy) {
    currentView.destroy();
  }

  if (viewEl) {
    const viewName = viewEl.getAttribute('data-view');
    if (views[viewName]) {
      currentView = views[viewName];
    }

    // Pass key functions to views
    if (typeof currentView !== 'undefined' && currentView && currentView.init) {
      currentView.init(getProps());
    }
  }

  triggerCallbacks();
}

function onAjaxLoad(data) {
  const content = data.split('<main role="main" id="main" class="content">')[1].split('</main>')[0] || '';
  const contentSelector = document.querySelector('.content');

  // Handle routing accessibility
  contentSelector.innerHTML = content;
  const elNavInfo = contentSelector.querySelector('.js-navigationInfo');
  if (elNavInfo) {
    elNavInfo.setAttribute('aria-hidden', 'false');
    elNavInfo.focus({ preventScroll: true });
  }

  head.render(head.extractMetaValues(data));
  initView();
  bind();

  setTimeout(() => {
    poly.removeClass(document.body, 'is-faded');
  }, 50);
  setTimeout(() => {
    poly.removeClass(document.body, 'is-showing-loader');
  }, TRANSITION_DURATION + 100);
}

function onState() {
  // Don't load if the state is the same
  if (window.location.pathname + window.location.search === currentState) {
    // if the user is on the home page scroll to top
    if (window.location.pathname === '/') {
      window.scrollTo(0, 0);
    }
    return;
  }

  poly.addClass(document.body, 'is-showing-loader');
  poly.addClass(document.body, 'is-faded');
  // Save the time to make sure the page fade-out-in transition finishes
  transitionTimer = Date.now();

  const request = new XMLHttpRequest();
  request.open('GET', window.location.pathname + window.location.search, true);

  request.onload = () => {
    const timeSinceTransition = Date.now() - transitionTimer;
    if (request.status >= 200 && request.status < 400) {
      // Success!
      if (timeSinceTransition > TRANSITION_DURATION) {
        onAjaxLoad(request.responseText);
      } else {
        setTimeout(() => {
          onAjaxLoad(request.responseText);
        }, TRANSITION_DURATION - timeSinceTransition);
      }
    } else {
      window.location.reload();
    }
  };

  request.onerror = () => {
    window.location.reload();
  };

  request.send();
  currentState = window.location.pathname + window.location.search;
}

function init() {
  window.addEventListener('popstate', onState);
  initView();
  bind();
}

export default {
  addCallback,
  bind,
  getProps,
  init,
};
